import { useNavigate } from 'react-router-dom';
import { Folder, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import FolderItem from './FolderItem';
import WorkspaceItem from './WorkspaceItem';

const WorkspacesList = ({
  folders,
  unfiledWorkspaces,
  workspaces,
  expandedFolders,
  expandedWorkspaces,
  workspaceProjects,
  loadingProjects,
  currentWorkspace,
  activeProjectId,
  onToggleFolder,
  onToggleWorkspace,
  onSwitchWorkspace,
  onCreateFolderClick,
  onEditFolder,
  onCreateProject,
  onMoveToFolder,
  onInvite,
  onOpenSettings,
  onOpenProjectSettings,
  workspaceUnread = {},
  projectUnread = {}
}) => {
  const navigate = useNavigate();

  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Workspaces</span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-[#4A9CC7]"
            onClick={onCreateFolderClick}
            title="Create Folder"
            data-testid="create-folder-sidebar-btn"
          >
            <Folder className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-[#4A9CC7]"
            onClick={() => navigate('/workspaces')}
            title="Manage Workspaces"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-1">
        {/* Render Folders (alphabetically sorted from API) */}
        {folders && folders.map(folder => (
          <FolderItem
            key={folder.folder_id}
            folder={folder}
            isExpanded={expandedFolders[folder.folder_id]}
            onToggle={() => onToggleFolder(folder.folder_id)}
            expandedWorkspaces={expandedWorkspaces}
            workspaceProjects={workspaceProjects}
            loadingProjects={loadingProjects}
            currentWorkspace={currentWorkspace}
            activeProjectId={activeProjectId}
            onToggleWorkspace={onToggleWorkspace}
            onSwitchWorkspace={onSwitchWorkspace}
            onEditFolder={onEditFolder}
            onCreateProject={onCreateProject}
            onMoveToFolder={onMoveToFolder}
            onInvite={onInvite}
            onOpenSettings={onOpenSettings}
            onOpenProjectSettings={onOpenProjectSettings}
            workspaceUnread={workspaceUnread}
            projectUnread={projectUnread}
          />
        ))}

        {/* Render Unfiled Workspaces (not in any folder) */}
        {unfiledWorkspaces && unfiledWorkspaces.map(ws => (
          <WorkspaceItem
            key={ws.workspace_id}
            workspace={ws}
            isNested={false}
            isExpanded={expandedWorkspaces[ws.workspace_id]}
            isCurrentWorkspace={currentWorkspace?.workspace_id === ws.workspace_id}
            onToggle={() => onToggleWorkspace(ws.workspace_id)}
            projects={workspaceProjects[ws.workspace_id]}
            loadingProjects={loadingProjects[ws.workspace_id]}
            activeProjectId={activeProjectId}
            onSwitchWorkspace={onSwitchWorkspace}
            onCreateProject={onCreateProject}
            onMoveToFolder={onMoveToFolder}
            onInvite={onInvite}
            onOpenSettings={onOpenSettings}
            onOpenProjectSettings={onOpenProjectSettings}
            workspaceUnread={workspaceUnread}
            projectUnread={projectUnread}
          />
        ))}

        {/* Fallback: Show all workspaces if no folder structure yet */}
        {(!folders || folders.length === 0) && (!unfiledWorkspaces || unfiledWorkspaces.length === 0) && workspaces.map(ws => (
          <WorkspaceItem
            key={ws.workspace_id}
            workspace={ws}
            isNested={false}
            isExpanded={expandedWorkspaces[ws.workspace_id]}
            isCurrentWorkspace={currentWorkspace?.workspace_id === ws.workspace_id}
            onToggle={() => onToggleWorkspace(ws.workspace_id)}
            projects={workspaceProjects[ws.workspace_id]}
            loadingProjects={loadingProjects[ws.workspace_id]}
            activeProjectId={activeProjectId}
            onSwitchWorkspace={onSwitchWorkspace}
            onCreateProject={onCreateProject}
            onMoveToFolder={onMoveToFolder}
            onInvite={onInvite}
            onOpenSettings={onOpenSettings}
            onOpenProjectSettings={onOpenProjectSettings}
            workspaceUnread={workspaceUnread}
            projectUnread={projectUnread}
          />
        ))}
      </div>
    </div>
  );
};

export default WorkspacesList;
